import { ReportContextFields } from './ReportContext';
import { LocalMediaPreview, MediaPreview } from './MediaPreview';
import {
  MEDIA_ACCEPT,
  type IntakePrefill,
  type ReportContext,
  DEFAULT_LIMITS,
  type Priority,
  type TicketDraft,
  type TicketType,
} from '@lixionary-jira/domain';
import { useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { ApiError } from '../../api/client';
import type { TicketCreation } from '../../api/creation';
import { useAction } from '../../api/hooks';
import { Button } from '../../ui/Button';
import { Alert, ErrorAlert } from '../../ui/feedback';
import { Field, FormSection, Input, ListEditor, Segmented, Select, Textarea } from '../../ui/forms';
import { Markdown } from '../../ui/Markdown';
import { AssistantDraft } from './AssistantDraft';
import { PRIORITY_OPTIONS } from './bits';

export interface SuppliedAttachment {
  id: string;
  filename: string;
  detectedType: string;
  sizeBytes: number;
  scanState: string;
}

type Mode = 'form' | 'assistant';

export interface CreatedTicket {
  key: string;
  number: number;
}

/**
 * TKT-01 ticket creation: the form (reviewed by the assistant in the background) or a drafting conversation
 * with the assistant. Shared by the project page and the external-intake popup (JIRA-19).
 */
export function TicketCreator({
  creation,
  assistant,
  initialType = 'issue',
  prefill = {},
  suppliedAttachments = [],
  suppliedBy,
  uploadLimits = {
    file: DEFAULT_LIMITS.uploadMaxBytes,
    video: DEFAULT_LIMITS.uploadVideoMaxBytes,
    count: DEFAULT_LIMITS.uploadMaxLiveFilesPerTicket,
  },
  allowPriority = false,
  allowAttachments = true,
  cancel,
  onCreated,
}: {
  creation: TicketCreation;
  assistant: { available: boolean; model: string; reviewOnCreate: boolean };
  initialType?: TicketType;
  prefill?: IntakePrefill;
  suppliedAttachments?: SuppliedAttachment[];
  suppliedBy?: string;
  uploadLimits?: { file: number; video: number; count: number };
  /** Admins set priority when creating; everyone else starts at normal. */
  allowPriority?: boolean;
  allowAttachments?: boolean;
  cancel: ReactNode;
  onCreated: (ticket: CreatedTicket) => void;
}) {
  const [params, setParams] = useSearchParams();
  const assistantOn = assistant.available;
  const mode: Mode = assistantOn && params.get('mode') === 'assistant' ? 'assistant' : 'form';
  const sessionId = params.get('conversation');
  const setMode = (m: Mode, conversation = sessionId) =>
    setParams(
      {
        ...(m === 'assistant' ? { mode: m } : {}),
        ...(conversation ? { conversation } : {}),
      },
      { replace: true },
    );
  /** Set when the form was prefilled from an assistant draft: submitting closes that conversation. */
  const [fromDraft, setFromDraft] = useState<string | null>(null);
  const [type, setType] = useState<TicketType>(initialType);
  const [title, setTitle] = useState(prefill.title ?? '');
  const [description, setDescription] = useState(prefill.description ?? '');
  const [preview, setPreview] = useState(false);
  const [steps, setSteps] = useState(prefill.stepsOrOutcome ?? '');
  const [context, setContext] = useState<ReportContext>({
    screenName: prefill.screenName ?? '',
    screenUrl: prefill.screenUrl ?? '',
    exampleData: prefill.exampleData ?? '',
  });
  const [supplied, setSupplied] = useState(() => suppliedAttachments.map((f) => f.id));
  const createdRef = useRef<CreatedTicket | null>(null);
  const [created, setCreated] = useState<CreatedTicket | null>(null);
  const pendingRequest = useRef<{ path: string; body: unknown; key: string } | null>(null);
  const [creationUncertain, setCreationUncertain] = useState(false);
  const uploadKeys = useRef(new Map<File, string>());
  const uploaded = useRef(new Set<File>());
  const [criteria, setCriteria] = useState<string[]>(['']);
  const [labels, setLabels] = useState('');
  const [priority, setPriority] = useState<Priority>('normal');
  const [files, setFiles] = useState<File[]>([]);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const { client, base, scope } = creation;
  const applyDraft = (d: TicketDraft, conversation: string) => {
    setType(d.type);
    setTitle(d.title);
    setDescription(d.description);
    setSteps(d.stepsOrOutcome);
    setCriteria(d.acceptanceCriteria.length ? d.acceptanceCriteria : ['']);
    setLabels(d.labels.join(', '));
    setFromDraft(conversation);
    setMode('form', conversation);
  };
  const create = useAction(
    async (submission: { draft: TicketDraft; conversation: string } | undefined) => {
      const body = {
        type,
        title,
        description,
        stepsOrOutcome: steps,
        acceptanceCriteria: criteria.filter((c) => c.trim()),
        labels: labels
          .split(',')
          .map((l) => l.trim())
          .filter(Boolean),
        ...(submission?.draft ?? {}),
        ...context,
        attachmentIds: supplied,
      };
      const conversation = submission?.conversation ?? fromDraft;
      let r = createdRef.current;
      if (!r) {
        pendingRequest.current ??= {
          path: conversation ? `${base}/assistant/sessions/${conversation}/submit` : `${base}/tickets`,
          body: { ...body, ...(!conversation && allowPriority ? { priority } : {}) },
          key: crypto.randomUUID(),
        };
        try {
          const request = pendingRequest.current;
          r = await client.post<CreatedTicket>(request.path, request.body, request.key);
          setCreationUncertain(false);
        } catch (e) {
          if (e instanceof ApiError && e.status < 500 && e.code !== 'idempotency_conflict') {
            pendingRequest.current = null;
            setCreationUncertain(false);
          } else setCreationUncertain(true);
          throw e;
        }
        createdRef.current = r;
        setCreated(r);
      }
      const errors: string[] = [];
      for (const f of files) {
        if (uploaded.current.has(f)) continue;
        const form = new FormData();
        form.append('file', f);
        const operationKey = uploadKeys.current.get(f) ?? crypto.randomUUID();
        uploadKeys.current.set(f, operationKey);
        await client
          .upload(`${base}/tickets/${r.number}/attachments`, form, operationKey)
          .then(() => {
            uploaded.current.add(f);
          })
          .catch((e: unknown) =>
            errors.push(`${f.name}: ${e instanceof Error ? e.message : 'upload failed'}`),
          );
      }
      setUploadErrors(errors);
      return { ...r, uploadErrors: errors };
    },
    {
      success: (r) => `Submitted ${r.key}`,
      invalidate: [
        ['board', scope],
        ['dashboard', scope],
      ],
      onSuccess: (r) => r.uploadErrors.length === 0 && onCreated(r),
    },
  );
  const err = create.error instanceof ApiError ? create.error : null;
  return (
    <>
      {assistantOn && (
        <Segmented<Mode>
          label="How to create the ticket"
          value={mode}
          onChange={(m) => setMode(m)}
          options={[
            { value: 'form', label: 'Fill in the form' },
            { value: 'assistant', label: 'Draft with the assistant' },
          ]}
        />
      )}
      {mode === 'assistant' ? (
        <AssistantDraft
          creation={creation}
          sessionId={sessionId}
          model={assistant.model}
          onSession={(id) => setMode('assistant', id)}
          onEdit={applyDraft}
          onSubmitted={() => undefined}
          requestContext={{ title, description, stepsOrOutcome: steps, ...context }}
          submitTicket={async (draft, conversation) => {
            const result = await create.mutateAsync({ draft, conversation });
            if (result.uploadErrors.length)
              throw new Error('The ticket was created. Retry the failed attachments below.');
            return result;
          }}
        />
      ) : (
        <form
          id="ticket-creation-form"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(undefined);
          }}
        >
          <fieldset
            disabled={create.isPending || !!created || creationUncertain}
            style={{ border: 0, padding: 0, minWidth: 0 }}
          >
            {fromDraft ? (
              <Alert tone="info" title="Prefilled from your assistant conversation">
                Review and adjust anything before submitting. The conversation and its code analysis stay
                linked to the ticket.
              </Alert>
            ) : (
              assistant.reviewOnCreate && (
                <p className="small muted">
                  After you submit, the assistant checks the request against the project&apos;s code and
                  suggests clarifications on the ticket. Your text is not changed unless you apply a
                  suggestion.
                </p>
              )
            )}
            <FormSection title="Request">
              <Segmented
                label="Ticket type"
                value={type}
                onChange={setType}
                options={[
                  { value: 'issue', label: 'Issue' },
                  { value: 'feature_request', label: 'Feature request' },
                ]}
              />
              <Field label="Title" error={err?.fieldError('title')}>
                {(p) => (
                  <Input
                    {...p}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={
                      type === 'issue'
                        ? 'e.g. Offline queue drops edits after token refresh'
                        : 'e.g. Export visit notes as PDF'
                    }
                    maxLength={200}
                    required
                  />
                )}
              </Field>
              <Field
                label="Description"
                hint="Markdown. HTML and remote images are not rendered."
                error={err?.fieldError('description')}
              >
                {(p) => (
                  <div className="stack sm">
                    <Segmented
                      label="Description mode"
                      value={preview ? 'preview' : 'write'}
                      onChange={(v) => setPreview(v === 'preview')}
                      options={[
                        { value: 'write', label: 'Write' },
                        { value: 'preview', label: 'Preview' },
                      ]}
                    />
                    {preview ? (
                      <div className="well">
                        <Markdown>{description || '_Nothing to preview_'}</Markdown>
                      </div>
                    ) : (
                      <Textarea
                        {...p}
                        rows={8}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        required
                      />
                    )}
                  </div>
                )}
              </Field>
              <Field label={type === 'issue' ? 'Reproduction steps' : 'Intended outcome'} optional>
                {(p) => (
                  <Textarea
                    {...p}
                    rows={4}
                    value={steps}
                    onChange={(e) => setSteps(e.target.value)}
                    placeholder={
                      type === 'issue'
                        ? '1. …  2. …  3. Expected vs actual'
                        : 'What a user can do once this ships'
                    }
                  />
                )}
              </Field>
            </FormSection>
            <FormSection
              title="Acceptance criteria"
              description="Proposed criteria. An admin finalizes them; they are required at acceptance."
            >
              <ListEditor
                label="Criterion"
                items={criteria}
                onChange={setCriteria}
                addLabel="Add criterion"
                placeholder="Observable outcome"
              />
            </FormSection>
            <FormSection title="Details">
              <Field label="Labels" hint="Comma separated." optional>
                {(p) => (
                  <Input
                    {...p}
                    value={labels}
                    onChange={(e) => setLabels(e.target.value)}
                    placeholder="sync, android"
                  />
                )}
              </Field>
              {allowPriority && !fromDraft ? (
                <Field label="Priority">
                  {(p) => (
                    <Select {...p} value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
                      {PRIORITY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              ) : (
                <p className="small muted">New requests start at normal priority; admins prioritize.</p>
              )}
            </FormSection>
          </fieldset>
          <ErrorAlert error={create.error} />
        </form>
      )}
      <fieldset
        disabled={create.isPending || !!created || creationUncertain}
        style={{ border: 0, padding: 0, minWidth: 0 }}
      >
        <FormSection
          title="Report context"
          description={
            suppliedBy
              ? `Supplied by ${suppliedBy}. Review, edit, or remove this context before submitting.`
              : 'Optional details to help the team understand your request.'
          }
        >
          <ReportContextFields value={context} onChange={setContext} />
          {allowAttachments && (
            <Field
              label="Attachments"
              optional
              hint={`Images, text, logs, JSON, CSV or PDF up to ${Math.round(uploadLimits.file / 1048576)} MiB; MP4, WebM or MOV up to ${Math.round(uploadLimits.video / 1048576)} MiB. Up to ${uploadLimits.count} files per ticket. Files follow the configured scan policy.`}
            >
              {(p) => (
                <input
                  {...p}
                  type="file"
                  multiple
                  accept={MEDIA_ACCEPT}
                  onChange={(e) => {
                    const next = [...files, ...(e.target.files ?? [])];
                    const errors = next.flatMap((f) =>
                      f.size > (f.type.startsWith('video/') ? uploadLimits.video : uploadLimits.file)
                        ? [`${f.name}: file exceeds the size limit.`]
                        : [],
                    );
                    if (next.length + supplied.length > uploadLimits.count)
                      errors.push(`Choose at most ${uploadLimits.count} files.`);
                    setUploadErrors(errors);
                    if (!errors.length) setFiles(next);
                    e.target.value = '';
                  }}
                />
              )}
            </Field>
          )}
          {suppliedAttachments
            .filter((f) => supplied.includes(f.id))
            .map((f) => (
              <div key={f.id} className="well stack sm">
                <span>
                  {f.filename} · supplied by {suppliedBy ?? 'the reporting app'} · {f.scanState}
                </span>
                {f.scanState === 'clean' && (
                  <MediaPreview
                    src={`/api/intake/v1/attachments/${f.id}/preview`}
                    type={f.detectedType}
                    name={f.filename}
                  />
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setSupplied(supplied.filter((id) => id !== f.id))}
                >
                  Remove {f.filename}
                </Button>
              </div>
            ))}
          {files.map((f, i) => (
            <div key={i} className="well stack sm">
              <span>{f.name}</span>
              <LocalMediaPreview file={f} />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setFiles(files.filter((_, n) => n !== i))}
              >
                Remove {f.name}
              </Button>
            </div>
          ))}
        </FormSection>
      </fieldset>
      {uploadErrors.length > 0 && (
        <Alert
          tone="warning"
          title={
            created ? `${created.key} was created, but some files were refused` : 'Check your attachments'
          }
        >
          <ul>
            {uploadErrors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
          {created && (
            <div className="row">
              <Button size="sm" loading={create.isPending} onClick={() => create.mutate(undefined)}>
                Retry failed uploads
              </Button>
              <Button size="sm" variant="secondary" onClick={() => onCreated(created)}>
                Continue to {created.key}
              </Button>
            </div>
          )}
        </Alert>
      )}
      {mode === 'assistant' && <ErrorAlert error={create.error} />}
      {creationUncertain && (
        <Alert tone="warning" title="Submission could not be confirmed">
          Retry to recover the same request without creating another ticket.
          {mode === 'assistant' && (
            <Button size="sm" loading={create.isPending} onClick={() => create.mutate(undefined)}>
              Retry submission
            </Button>
          )}
        </Alert>
      )}
      {mode === 'form' && (
        <div className="form-actions">
          {cancel}
          <Button type="submit" form="ticket-creation-form" disabled={!!created} loading={create.isPending}>
            {creationUncertain ? 'Retry submission' : 'Submit request'}
          </Button>
        </div>
      )}
    </>
  );
}
